import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { Search } from 'lucide-react';
import { ALL_COUNTRIES, isEnglishPrimaryCountry } from '../../data/countries';

// ─── Searchable country dropdown ───
const CountrySelect = ({ value, onChange, error }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = search
    ? ALL_COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()))
    : ALL_COUNTRIES;

  return (
    <div className="relative">
      <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
        Where are you from? <span style={{ color: 'var(--gold)' }}>*</span>
      </label>

      <div
        className={cn(
          "w-full px-4 py-3 bg-gray-900/40 border rounded-lg text-[#EEF2F7] cursor-pointer flex items-center justify-between",
          open ? "border-[#C9A84C] ring-2 ring-[rgba(201,168,76,0.1)]" : "border-[rgba(201,168,76,0.15)]"
        )}
        onClick={() => setOpen(!open)}
        data-testid="input-country"
      >
        <span className={value ? "text-[#EEF2F7]" : "text-[#8D97A8]"}>
          {value || 'Select your country'}
        </span>
        <svg className={cn("w-4 h-4 text-[#8D97A8] transition-transform", open && "rotate-180")} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#111] border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-800 flex items-center gap-2">
            <Search className="w-4 h-4 text-[#8D97A8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              autoFocus
              className="w-full bg-transparent text-[#EEF2F7] placeholder-[#8D97A8] text-sm outline-none"
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.map((c) => (
              <div
                key={c}
                onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                className={cn(
                  "px-4 py-2 text-sm cursor-pointer transition-colors",
                  c === value ? "bg-[#C63F5C]/20 text-[#E05672]" : "text-[#EEF2F7] hover:bg-gray-800"
                )}
              >
                {c}
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="px-4 py-3 text-sm text-[#8D97A8]">No countries found</p>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-[#C9A84C] text-sm mt-2">{error}</p>}
    </div>
  );
};

// ─── Step 1: Contact + Trading Profile ───
export const Step1TradingProfile = ({ formData, updateFormData, errors }) => {
  return (
    <div className="space-y-6">
      {/* Contact fields first */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
            First Name <span className="text-[#C9A84C]">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName || ''}
            onChange={(e) => updateFormData({ firstName: e.target.value })}
            placeholder="John"
            data-testid="input-first-name"
            className="w-full px-4 py-3 g-input"
          />
          {errors.firstName && <p className="text-[#C9A84C] text-sm mt-2">{errors.firstName}</p>}
        </div>
        <div>
          <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>Last Name</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName || ''}
            onChange={(e) => updateFormData({ lastName: e.target.value })}
            placeholder="Doe"
            data-testid="input-last-name"
            className="w-full px-4 py-3 g-input"
          />
        </div>
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          Email <span className="text-[#C9A84C]">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email || ''}
          onChange={(e) => updateFormData({ email: e.target.value })}
          placeholder="john@example.com"
          data-testid="input-email"
          className="w-full px-4 py-3 g-input"
        />
        {errors.email && <p className="text-[#C9A84C] text-sm mt-2">{errors.email}</p>}
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          Phone <span className="text-[#C9A84C]">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone || ''}
          onChange={(e) => updateFormData({ phone: e.target.value })}
          placeholder="+1 (555) 123-4567"
          data-testid="input-phone"
          className="w-full px-4 py-3 g-input"
        />
        {errors.phone && <p className="text-[#C9A84C] text-sm mt-2">{errors.phone}</p>}
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          Instagram Handle <span className="text-[#C9A84C]">*</span>
        </label>
        <input
          type="text"
          name="instagram"
          value={formData.instagram || ''}
          onChange={(e) => updateFormData({ instagram: e.target.value })}
          placeholder="@yourusername"
          data-testid="input-instagram"
          className="w-full px-4 py-3 g-input"
        />
        <p className="text-sm text-[#8D97A8] mt-2">
          We check this privately to understand where you are in your trading journey. We won't post, tag, or contact you through Instagram.
        </p>
        {errors.instagram && <p className="text-[#C9A84C] text-sm mt-2">{errors.instagram}</p>}
      </div>

      <CountrySelect
        value={formData.country || ''}
        onChange={(val) => updateFormData({ country: val, speaksEnglish: '' })}
        error={errors.country}
      />

      {formData.country && !isEnglishPrimaryCountry(formData.country) && (
        <div>
          <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
            Do you speak English? <span className="text-[#C9A84C]">*</span>
          </label>
          <div className="space-y-3">
            {[
              { value: 'Yes, fluently', label: 'Yes, fluently' },
              { value: 'Decent', label: "It's decent — not perfect, but I can follow along" },
              { value: 'No', label: "No, I don't speak English" },
            ].map((opt) => (
              <label
                key={opt.value}
                className={cn(
                  "flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-200",
                  formData.speaksEnglish === opt.value ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
                )}
              >
                <input
                  type="radio"
                  name="speaksEnglish"
                  value={opt.value}
                  checked={formData.speaksEnglish === opt.value}
                  onChange={(e) => updateFormData({ speaksEnglish: e.target.value })}
                  className="w-4 h-4 g-radio"
                />
                <span className="ml-3 text-[#EEF2F7]">{opt.label}</span>
              </label>
            ))}
          </div>
          {errors.speaksEnglish && <p className="text-[#C9A84C] text-sm mt-2">{errors.speaksEnglish}</p>}
        </div>
      )}

      <hr className="border-gray-700/50" />

      {/* Trading questions */}
      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          How long have you been trading? <span className="text-[#C9A84C]">*</span>
        </label>
        <div className="space-y-3">
          {["I'm a complete beginner", "Less than 6 months", "6 months – 1 year", "1–2 years", "2+ years"].map((option) => (
            <label
              key={option}
              className={cn(
                "flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-200",
                formData.tradingExperience === option ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
              )}
            >
              <input type="radio" name="tradingExperience" value={option} checked={formData.tradingExperience === option} onChange={(e) => updateFormData({ tradingExperience: e.target.value })} className="w-4 h-4 g-radio" />
              <span className="ml-3 text-[#EEF2F7]">{option}</span>
            </label>
          ))}
        </div>
        {errors.tradingExperience && <p className="text-[#C9A84C] text-sm mt-2">{errors.tradingExperience}</p>}
      </div>
    </div>
  );
};

// ─── Step 2: Current Struggle (unchanged) ───
export const Step2CurrentStruggle = ({ formData, updateFormData, errors }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          What's your biggest struggle in trading right now? (Select all that apply) <span className="text-[#C9A84C]">*</span>
        </label>
        <div className="space-y-3">
          {["Risk management", "Entries / timing", "Consistency", "Psychology", "Reading order flow", "Overtrading", "Failing evals / blowing accounts", "Trade management", "Lack of clear system"].map((option) => (
            <label
              key={option}
              className={cn(
                "flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-200",
                formData.biggestStruggle?.includes(option) ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
              )}
            >
              <input
                type="checkbox"
                name="biggestStruggle"
                value={option}
                checked={formData.biggestStruggle?.includes(option)}
                onChange={(e) => {
                  const cur = formData.biggestStruggle || [];
                  updateFormData({ biggestStruggle: e.target.checked ? [...cur, option] : cur.filter(s => s !== option) });
                }}
                className="w-4 h-4 text-[#C63F5C] border-gray-600 rounded focus:ring-[#C63F5C] focus:ring-2"
              />
              <span className="ml-3 text-[#EEF2F7]">{option}</span>
            </label>
          ))}
        </div>
        {errors.biggestStruggle && <p className="text-[#C9A84C] text-sm mt-2">{errors.biggestStruggle}</p>}
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          What has kept you from consistency so far? <span className="text-[#8D97A8] font-normal">(Optional)</span>
        </label>
        <textarea
          name="consistencyBlocks"
          value={formData.consistencyBlocks || ''}
          onChange={(e) => updateFormData({ consistencyBlocks: e.target.value })}
          rows={5}
          placeholder="Share your thoughts..."
          className="w-full px-4 py-3 g-input"
        />
      </div>
    </div>
  );
};

// ─── Step 3: Seriousness / Fit ───
export const Step3SeriousnessFit = ({ formData, updateFormData, errors }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          How serious are you about improving your trading over the next 90 days? <span className="text-[#C9A84C]">*</span>
        </label>
        <div className="space-y-3">
          {["Curious, but not fully committed", "I want to improve, but I haven't treated it seriously enough yet", "I'm serious and ready to put in the work", "I'm fully committed to becoming consistently profitable"].map((option) => (
            <label
              key={option}
              className={cn(
                "flex items-start p-4 rounded-lg border cursor-pointer transition-all duration-200",
                formData.seriousness === option ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
              )}
            >
              <input type="radio" name="seriousness" value={option} checked={formData.seriousness === option} onChange={(e) => updateFormData({ seriousness: e.target.value })} className="w-4 h-4 g-radio mt-0.5" />
              <span className="ml-3 text-[#EEF2F7]">{option}</span>
            </label>
          ))}
        </div>
        {errors.seriousness && <p className="text-[#C9A84C] text-sm mt-2">{errors.seriousness}</p>}
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          Why do you want to join Order Flow School specifically? <span className="text-[#8D97A8] font-normal">(Optional)</span>
        </label>
        <textarea
          name="whyOFS"
          value={formData.whyOFS || ''}
          onChange={(e) => updateFormData({ whyOFS: e.target.value })}
          rows={5}
          placeholder="Tell us what drew you to Order Flow School..."
          className="w-full px-4 py-3 g-input"
        />
        <p className="text-sm text-[#8D97A8] mt-2 italic">Optional — answering this can increase your chances of being accepted.</p>
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          If accepted, how ready are you to implement a structured training framework? <span className="text-[#C9A84C]">*</span>
        </label>
        <div className="space-y-3">
          {["Just exploring", "Interested but unsure", "Ready to commit", "Fully ready"].map((option) => (
            <label
              key={option}
              className={cn(
                "flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-200",
                formData.readiness === option ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
              )}
            >
              <input type="radio" name="readiness" value={option} checked={formData.readiness === option} onChange={(e) => updateFormData({ readiness: e.target.value })} className="w-4 h-4 g-radio" />
              <span className="ml-3 text-[#EEF2F7]">{option}</span>
            </label>
          ))}
        </div>
        {errors.readiness && <p className="text-[#C9A84C] text-sm mt-2">{errors.readiness}</p>}
      </div>
    </div>
  );
};

// ─── Step 4: Investment + Call ───
export const Step4InvestmentQualification = ({ formData, updateFormData, errors }) => {
  return (
    <div className="space-y-6">
      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          If accepted, how much are you prepared to invest in your trading education? <span className="text-[#C9A84C]">*</span>
        </label>
        <div className="space-y-3">
          {["$0-$500", "$500-$1,000", "$1,000-$2,500", "$2,500-$4,000", "$4,000+"].map((option) => (
            <label
              key={option}
              className={cn(
                "flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-200",
                formData.investment === option ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
              )}
            >
              <input type="radio" name="investment" value={option} checked={formData.investment === option} onChange={(e) => updateFormData({ investment: e.target.value })} className="w-4 h-4 g-radio" />
              <span className="ml-3 text-[#EEF2F7]">{option}</span>
            </label>
          ))}
        </div>
        {errors.investment && <p className="text-[#C9A84C] text-sm mt-2">{errors.investment}</p>}
      </div>

      <div>
        <label className="block font-semibold mb-3" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)' }}>
          Are you willing to get on a short call if your application is approved? <span className="text-[#C9A84C]">*</span>
        </label>
        <div className="space-y-3">
          {["Yes", "No"].map((option) => (
            <label
              key={option}
              className={cn(
                "flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-200",
                formData.callWillingness === option ? "border-[#C9A84C] bg-[rgba(201,168,76,0.08)]" : "border-[rgba(201,168,76,0.15)] bg-[#0D1420] hover:border-[rgba(201,168,76,0.3)]"
              )}
            >
              <input type="radio" name="callWillingness" value={option} checked={formData.callWillingness === option} onChange={(e) => updateFormData({ callWillingness: e.target.value })} className="w-4 h-4 g-radio" />
              <span className="ml-3 text-[#EEF2F7]">{option}</span>
            </label>
          ))}
        </div>
        {errors.callWillingness && <p className="text-[#C9A84C] text-sm mt-2">{errors.callWillingness}</p>}
      </div>
    </div>
  );
};

// ─── Step 5: Review & Submit ───
export const Step5ReviewSubmit = ({ formData }) => {
  const sections = [
    {
      title: 'Contact Information',
      items: [
        { label: 'Name', value: [formData.firstName, formData.lastName].filter(Boolean).join(' ') },
        { label: 'Email', value: formData.email },
        { label: 'Phone', value: formData.phone },
        { label: 'Instagram', value: formData.instagram },
        { label: 'Country', value: formData.country },
        ...(formData.speaksEnglish ? [{ label: 'English', value: formData.speaksEnglish }] : []),
      ],
    },
    {
      title: 'Trading Profile',
      items: [
        { label: 'Experience', value: formData.tradingExperience },
        { label: 'Instruments', value: (formData.tradingAssets || []).join(', ') },
      ],
    },
    {
      title: 'Current Struggles',
      items: [
        { label: 'Biggest struggles', value: (formData.biggestStruggle || []).join(', ') },
        ...(formData.consistencyBlocks ? [{ label: 'Consistency blocks', value: formData.consistencyBlocks }] : []),
      ],
    },
    {
      title: 'Commitment',
      items: [
        { label: 'Seriousness', value: formData.seriousness },
        { label: 'Readiness', value: formData.readiness },
        ...(formData.whyOFS ? [{ label: 'Why OFS', value: formData.whyOFS }] : []),
      ],
    },
    {
      title: 'Qualification',
      items: [
        { label: 'Investment', value: formData.investment },
        { label: 'Call willingness', value: formData.callWillingness },
      ],
    },
  ];

  return (
    <div className="space-y-6" data-testid="review-submit-step">
      <div className="text-center space-y-2 mb-8">
        <h2 className="text-2xl font-bold text-white">Review Your Application</h2>
        <p className="text-[#8D97A8]">Please review your answers before submitting.</p>
      </div>

      {sections.map((section) => (
        <div key={section.title} className="rounded-lg border border-gray-700/50 bg-gray-900/40 p-5 space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-[#E05672]">{section.title}</h3>
          {section.items.map((item) => (
            <div key={item.label} className="flex flex-col sm:flex-row sm:justify-between gap-1">
              <span className="text-[#8D97A8] text-sm">{item.label}</span>
              <span className="text-[#EEF2F7] text-sm sm:text-right max-w-xs break-words">{item.value || '—'}</span>
            </div>
          ))}
        </div>
      ))}

      <div className="bg-[#0A0A0A] border border-gray-800/50 rounded-lg p-4">
        <p className="text-sm text-[#8D97A8] leading-relaxed">
          By submitting this application, you agree to receive email and SMS communication regarding your application status and interview scheduling.
        </p>
      </div>
    </div>
  );
};
